import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardBody, Button, LoadingSpinner } from '../common';
import { SubmissionPicker } from './SubmissionPicker';
import { PassageBuilder } from './PassageBuilder';
import { GMTextEditor } from './GMTextEditor';
import { PassagePreview } from './PassagePreview';
import { SubmissionDetailModal } from './SubmissionDetailModal';
import { PassageEditorModal } from './PassageEditorModal';
import { useAllSubmissions } from '../../hooks/useSubmissions';
import { usePassage, useUpdatePassage, usePublishPassage } from '../../hooks/usePassages';
import { uploadMedia } from '../../api/upload';
import { useAuth } from '../../hooks/useAuth';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/mp4', 'audio/x-m4a'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_AUDIO_SIZE = 10 * 1024 * 1024; // 10MB

export const CompositionEditor = ({ gameId, roundId, onPublished }) => {
  const { user } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [editingBlock, setEditingBlock] = useState(null);
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [isExpandedOpen, setIsExpandedOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Refs for file inputs
  const imageInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // Fetch submissions and passage data
  const { data: submissionsData, isLoading: submissionsLoading } = useAllSubmissions(gameId, roundId);
  const { data: passageData, isLoading: passageLoading } = usePassage(gameId, roundId);

  const updatePassage = useUpdatePassage(gameId, roundId);
  const publishPassage = usePublishPassage(gameId, roundId);

  // Initialize blocks from passage data
  useEffect(() => {
    if (passageData?.passage?.content) {
      setBlocks(passageData.passage.content.map((block, idx) => ({
        ...block,
        id: block.id || `block-${idx}`
      })));
    }
  }, [passageData]);

  // Auto-save when dirty
  const saveChanges = useCallback(async (throwOnError = false) => {
    if (!isDirty) return true;
    setError(null);

    try {
      await updatePassage.mutateAsync({
        content: blocks.map((block, idx) => ({
          type: block.type,
          submissionId: block.submissionId,
          userId: block.userId,
          content: block.content,
          originalContent: block.originalContent,
          contentType: block.contentType,
          mediaUrl: block.mediaUrl,
          mediaKey: block.mediaKey,
          order: idx
        }))
      });
      setIsDirty(false);
      return true;
    } catch (err) {
      console.error('Failed to save passage:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save passage';
      setError(errorMessage);
      if (throwOnError) throw err;
      return false;
    }
  }, [isDirty, blocks, updatePassage]);

  // Auto-save every 5 seconds when dirty
  useEffect(() => {
    if (!isDirty) return;
    const timer = setTimeout(saveChanges, 5000);
    return () => clearTimeout(timer);
  }, [isDirty, saveChanges]);

  // Add content from submission
  const handleAddContent = (contentBlock) => {
    const newBlock = {
      ...contentBlock,
      id: `block-${Date.now()}`,
      order: blocks.length
    };
    setBlocks([...blocks, newBlock]);
    setIsDirty(true);
  };

  // Remove block
  const handleRemoveBlock = (index) => {
    const newBlocks = blocks.filter((_, i) => i !== index);
    setBlocks(newBlocks);
    setIsDirty(true);
  };

  // Move block
  const handleMoveBlock = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= blocks.length) return;
    const newBlocks = [...blocks];
    const [moved] = newBlocks.splice(fromIndex, 1);
    newBlocks.splice(toIndex, 0, moved);
    setBlocks(newBlocks);
    setIsDirty(true);
  };

  // Add GM text (replaces transition and prose)
  const handleAddGMText = () => {
    setEditingBlock({
      type: 'gm_text',
      content: '',
      contentType: 'prose',
      id: `block-${Date.now()}`
    });
  };

  // Add GM image - trigger file input
  const handleAddGMImage = () => {
    imageInputRef.current?.click();
  };

  // Add GM audio - trigger file input
  const handleAddGMAudio = () => {
    audioInputRef.current?.click();
  };

  // Handle image file selection
  const handleImageFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset input
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }

    setError(null);

    // Validate type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Invalid image type. Allowed: JPEG, PNG, GIF, WebP');
      return;
    }

    // Validate size
    if (file.size > MAX_IMAGE_SIZE) {
      setError('Image too large. Maximum: 5MB');
      return;
    }

    // Upload the file
    setUploading(true);
    try {
      const result = await uploadMedia(gameId, file, 'image');
      const newBlock = {
        type: 'gm_image',
        contentType: 'image',
        mediaUrl: result.media.url,
        mediaKey: result.media.storageKey,
        userId: user?.id,
        id: `block-${Date.now()}`,
        order: blocks.length
      };
      setBlocks([...blocks, newBlock]);
      setIsDirty(true);
    } catch (err) {
      console.error('Failed to upload image:', err);
      setError(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Handle audio file selection
  const handleAudioFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset input
    if (audioInputRef.current) {
      audioInputRef.current.value = '';
    }

    setError(null);

    // Validate type
    if (!ALLOWED_AUDIO_TYPES.includes(file.type)) {
      setError('Invalid audio type. Allowed: MP3, WAV, OGG, WebM, M4A');
      return;
    }

    // Validate size
    if (file.size > MAX_AUDIO_SIZE) {
      setError('Audio too large. Maximum: 10MB');
      return;
    }

    // Upload the file
    setUploading(true);
    try {
      const result = await uploadMedia(gameId, file, 'audio');
      const newBlock = {
        type: 'gm_audio',
        contentType: 'audio',
        mediaUrl: result.media.url,
        mediaKey: result.media.storageKey,
        userId: user?.id,
        id: `block-${Date.now()}`,
        order: blocks.length
      };
      setBlocks([...blocks, newBlock]);
      setIsDirty(true);
    } catch (err) {
      console.error('Failed to upload audio:', err);
      setError(err.response?.data?.error || 'Failed to upload audio');
    } finally {
      setUploading(false);
    }
  };

  // Edit existing text block (GM or player prose)
  const handleEditBlock = (block) => {
    // Allow editing text-based GM blocks
    if (block.type === 'gm_prose' || block.type === 'gm_transition' || block.type === 'gm_text') {
      setEditingBlock(block);
    }
    // Allow editing player prose blocks
    else if (block.type === 'player_content' && block.contentType === 'prose') {
      // If editing for first time, preserve original content
      if (!block.originalContent) {
        setEditingBlock({
          ...block,
          originalContent: block.content
        });
      } else {
        setEditingBlock(block);
      }
    }
  };

  // Save from expanded editor modal
  const handleExpandedSave = (updatedBlocks) => {
    setBlocks(updatedBlocks);
    setIsDirty(true);
  };

  // Save edited block
  const handleSaveEditedBlock = (block) => {
    const existingIndex = blocks.findIndex(b => b.id === block.id);
    if (existingIndex >= 0) {
      const newBlocks = [...blocks];
      newBlocks[existingIndex] = block;
      setBlocks(newBlocks);
    } else {
      setBlocks([...blocks, { ...block, order: blocks.length }]);
    }
    setEditingBlock(null);
    setIsDirty(true);
  };

  // Publish passage
  const handlePublish = async () => {
    setError(null);

    // Save first - if save fails, don't proceed to publish
    if (isDirty) {
      try {
        await saveChanges(true);
      } catch (err) {
        // Error already set in saveChanges
        return;
      }
    }

    try {
      await publishPassage.mutateAsync();
      onPublished?.();
    } catch (err) {
      console.error('Failed to publish passage:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to publish passage';
      setError(errorMessage);
    }
  };

  // Get added content (for tracking what's already in the passage)
  const addedContent = blocks
    .filter(b => b.type === 'player_content' && b.submissionId)
    .map(b => ({
      submissionId: b.submissionId,
      contentType: b.contentType,
      mediaKey: b.mediaKey,
      mediaUrl: b.mediaUrl
    }));

  if (submissionsLoading || passageLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const submissions = submissionsData?.submissions || [];

  return (
    <div className="space-y-4">
      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept={ALLOWED_IMAGE_TYPES.join(',')}
        onChange={handleImageFileSelect}
        className="hidden"
      />
      <input
        ref={audioInputRef}
        type="file"
        accept={ALLOWED_AUDIO_TYPES.join(',')}
        onChange={handleAudioFileSelect}
        className="hidden"
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-bold">Compose the Passage</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {isDirty && (
            <span className="text-sm text-yellow-600">Unsaved changes</span>
          )}
          {uploading && (
            <span className="text-sm text-blue-600">Uploading...</span>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Edit' : 'Preview'}
          </Button>
          <Button
            size="sm"
            onClick={handlePublish}
            loading={publishPassage.isPending || updatePassage.isPending}
            disabled={blocks.length === 0}
          >
            Publish Passage
          </Button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {showPreview ? (
        /* Preview Mode */
        <Card>
          <CardBody>
            <h3 className="font-semibold mb-4">Passage Preview</h3>
            <PassagePreview blocks={blocks} />
          </CardBody>
        </Card>
      ) : (
        /* Edit Mode */
        <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
          {/* Left: Submissions */}
          <Card>
            <CardBody>
              <SubmissionPicker
                submissions={submissions}
                onAddContent={handleAddContent}
                onViewFull={setViewingSubmission}
                addedContent={addedContent}
              />
            </CardBody>
          </Card>

          {/* Right: Passage Builder */}
          <Card>
            <CardBody>
              <PassageBuilder
                blocks={blocks}
                onRemoveBlock={handleRemoveBlock}
                onMoveBlock={handleMoveBlock}
                onEditBlock={handleEditBlock}
                onExpand={() => setIsExpandedOpen(true)}
                onAddGMText={handleAddGMText}
                onAddGMImage={handleAddGMImage}
                onAddGMAudio={handleAddGMAudio}
                uploading={uploading}
              />
            </CardBody>
          </Card>
        </div>
      )}

      {/* GM Text Editor Modal */}
      <GMTextEditor
        isOpen={!!editingBlock}
        block={editingBlock}
        onSave={handleSaveEditedBlock}
        onCancel={() => setEditingBlock(null)}
      />

      {/* Submission Detail Modal */}
      <SubmissionDetailModal
        isOpen={!!viewingSubmission}
        onClose={() => setViewingSubmission(null)}
        submission={viewingSubmission}
        onAddContent={handleAddContent}
        addedContent={addedContent}
      />

      {/* Expanded Passage Editor Modal */}
      <PassageEditorModal
        isOpen={isExpandedOpen}
        onClose={() => setIsExpandedOpen(false)}
        blocks={blocks}
        onSave={handleExpandedSave}
        onAddGMText={handleAddGMText}
        onAddGMImage={handleAddGMImage}
        onAddGMAudio={handleAddGMAudio}
        uploading={uploading}
        gameId={gameId}
      />

      {/* Stats */}
      <div className="text-sm text-gray-500">
        {new Set(addedContent.map(c => c.submissionId)).size} of {submissions.length} submissions used
        {isDirty && ' · '}
        {isDirty && <Button variant="link" size="sm" onClick={saveChanges}>Save now</Button>}
      </div>
    </div>
  );
};
